import React, { useState } from "react";
import axios from "axios";
import { X, Plus, Minus, Trash2, ShoppingBag, ChevronLeft, Gift } from "lucide-react";
import { useCart, useLang } from "../store";
import { useT } from "../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fixed product list for the free-cup picker (no network dependency)
const FREE_CUP_OPTIONS = [
  { id: "velora-pink", slug: "pink", name_en: "Strawberry Pink", name_es: "Rosa Fresa", hex: "#FF3D7F", price: 5.99 },
  { id: "velora-blue", slug: "blue", name_en: "Blue Raspberry", name_es: "Frambuesa Azul", hex: "#1FB6FF", price: 5.99 },
  { id: "velora-yellow", slug: "mango", name_en: "Mango Sunshine", name_es: "Mango Sol", hex: "#FFCC00", price: 5.99 },
  { id: "velora-green", slug: "lime", name_en: "Green Apple Lime", name_es: "Manzana Lima", hex: "#9BE800", price: 5.99 },
  { id: "velora-purple", slug: "grape", name_en: "Grape Galaxy", name_es: "Uva Galaxia", hex: "#8B5CF6", price: 5.99 },
  { id: "velora-orange", slug: "orange", name_en: "Orange Burst", name_es: "Naranja Explosión", hex: "#FF7A1A", price: 5.99 },
];

const t_form = {
  en: {
    shippingTitle: "Shipping details",
    name: "Full name", email: "Email", phone: "Phone",
    address1: "Street address", address2: "Apt, suite, etc. (optional)",
    city: "City", state: "State / Region", postal: "Postal code", country: "Country",
    notes: "Delivery notes (optional)",
    continue: "Continue to payment", back: "Back to cart",
    required: "Please fill all required fields",
  },
  es: {
    shippingTitle: "Datos de envío",
    name: "Nombre completo", email: "Email", phone: "Teléfono",
    address1: "Dirección", address2: "Piso, apto, etc. (opcional)",
    city: "Ciudad", state: "Provincia / Estado", postal: "Código postal", country: "País",
    notes: "Notas de entrega (opcional)",
    continue: "Continuar al pago", back: "Volver al carrito",
    required: "Por favor completa todos los campos obligatorios",
  },
};

export default function CartSheet() {
  const { lang } = useLang();
  const t = useT(lang);
  const tf = t_form[lang] || t_form.en;
  const { cart, cartOpen, setCartOpen, updateQty, removeItem, subtotal, addToCart } = useCart();
  const [step, setStep] = useState("cart"); // 'cart' | 'shipping'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [pickFree, setPickFree] = useState(false);

  const claimFreeCup = (product) => {
    addToCart(product);
    setPickFree(false);
  };
  const [ship, setShip] = useState({
    full_name: "", email: "", phone: "",
    address_line1: "", address_line2: "",
    city: "", state: "", postal_code: "",
    country: lang === "es" ? "España" : "United States",
    notes: "",
  });

  const onChange = (e) => setShip((s) => ({ ...s, [e.target.name]: e.target.value }));

  const validShipping = () => {
    return (
      ship.full_name.trim() && ship.email.includes("@") &&
      ship.address_line1.trim() && ship.city.trim() &&
      ship.postal_code.trim() && ship.country.trim()
    );
  };

  const goToShipping = () => {
    setError("");
    setStep("shipping");
  };

  const submitOrder = async (e) => {
    e?.preventDefault();
    if (!validShipping()) { setError(tf.required); return; }
    setLoading(true); setError("");
    try {
      const res = await axios.post(`${API}/checkout/session`, {
        items: cart.map((i) => ({ product_id: i.id, quantity: i.qty })),
        origin_url: window.location.origin,
        language: lang,
        customer_email: ship.email,
        shipping: ship,
      });
      window.location.href = res.data.url;
    } catch (e) {
      setError(e?.response?.data?.detail || "Checkout failed");
      setLoading(false);
    }
  };

  const closeAll = () => { setCartOpen(false); setStep("cart"); };

  const inputCls = "w-full bg-white border-[3px] border-[#0C0A09] px-3 py-2.5 font-medium text-sm focus:outline-none focus:shadow-[3px_3px_0_0_#0C0A09]";
  const labelCls = "text-[10px] font-bold uppercase tracking-[0.18em] block mb-1";

  return (
    <>
      <div
        onClick={closeAll}
        className={`fixed inset-0 bg-black/40 z-50 transition-opacity ${cartOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}
        data-testid="cart-backdrop"
      />
      <aside
        className={`fixed top-0 right-0 h-full w-full sm:w-[460px] bg-[#FAF9F6] z-50 border-l-[3px] border-[#0C0A09] shadow-[-8px_0_0_0_#0C0A09] transition-transform duration-300 flex flex-col ${cartOpen ? "translate-x-0" : "translate-x-full"}`}
        data-testid="cart-sheet"
      >
        <div className="flex items-center justify-between p-5 border-b-[3px] border-[#0C0A09]">
          <div className="flex items-center gap-2">
            {step === "shipping" ? (
              <button data-testid="shipping-back-btn" onClick={() => setStep("cart")} className="w-9 h-9 rounded-full border-[3px] border-[#0C0A09] bg-white flex items-center justify-center">
                <ChevronLeft size={16} strokeWidth={3} />
              </button>
            ) : (
              <ShoppingBag size={20} strokeWidth={2.5} />
            )}
            <h2 className="font-display text-2xl md:text-3xl tracking-tight">
              {step === "shipping" ? tf.shippingTitle : t.cart.title}
            </h2>
          </div>
          <button data-testid="close-cart-btn" onClick={closeAll} className="w-10 h-10 rounded-full bg-white border-[3px] border-[#0C0A09] flex items-center justify-center hover:-translate-y-0.5 transition-transform">
            <X size={18} strokeWidth={3} />
          </button>
        </div>

                <div className="flex-1 overflow-y-auto p-5">
          {(() => {
            // Flatten the nested ternary into explicit branches.
            if (step !== "cart") {
              return (
                <form onSubmit={submitOrder} className="space-y-3" data-testid="shipping-form">
                  <div>
                    <label className={labelCls}>{tf.name} *</label>
                    <input data-testid="ship-name" name="full_name" required value={ship.full_name} onChange={onChange} className={inputCls} />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={labelCls}>{tf.email} *</label>
                      <input data-testid="ship-email" name="email" type="email" required value={ship.email} onChange={onChange} className={inputCls} />
                    </div>
                    <div>
                      <label className={labelCls}>{tf.phone}</label>
                      <input data-testid="ship-phone" name="phone" value={ship.phone} onChange={onChange} className={inputCls} />
                    </div>
                  </div>
                  <div>
                    <label className={labelCls}>{tf.address1} *</label>
                    <input data-testid="ship-addr1" name="address_line1" required value={ship.address_line1} onChange={onChange} className={inputCls} />
                  </div>
                  <div>
                    <label className={labelCls}>{tf.address2}</label>
                    <input data-testid="ship-addr2" name="address_line2" value={ship.address_line2} onChange={onChange} className={inputCls} />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={labelCls}>{tf.city} *</label>
                      <input data-testid="ship-city" name="city" required value={ship.city} onChange={onChange} className={inputCls} />
                    </div>
                    <div>
                      <label className={labelCls}>{tf.postal} *</label>
                      <input data-testid="ship-postal" name="postal_code" required value={ship.postal_code} onChange={onChange} className={inputCls} />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={labelCls}>{tf.state}</label>
                      <input data-testid="ship-state" name="state" value={ship.state} onChange={onChange} className={inputCls} />
                    </div>
                    <div>
                      <label className={labelCls}>{tf.country} *</label>
                      <input data-testid="ship-country" name="country" required value={ship.country} onChange={onChange} className={inputCls} />
                    </div>
                  </div>
                  <div>
                    <label className={labelCls}>{tf.notes}</label>
                    <textarea data-testid="ship-notes" name="notes" value={ship.notes} onChange={onChange} rows={2} className={inputCls + " resize-none"} />
                  </div>
                </form>
              );
            }
            if (cart.length === 0) {
              return (
                <div data-testid="cart-empty" className="text-center py-20">
                  <div className="text-6xl mb-4">🥶</div>
                  <p className="font-bold uppercase tracking-widest text-sm">{t.cart.empty}</p>
                  <button onClick={closeAll} className="nb-btn secondary mt-6">{t.cart.emptyCta}</button>
                </div>
              );
            }
            return (
              <div className="space-y-3">
                {cart.map((item) => (
                  <div key={item.id} className="bg-white border-[3px] border-[#0C0A09] p-4 flex gap-3" data-testid={`cart-item-${item.id}`}>
                    <div className="w-16 h-16 rounded-full border-[3px] border-[#0C0A09] flex-shrink-0" style={{ background: item.hex }} />
                    <div className="flex-1">
                      <div className="font-display text-xl leading-none">{lang === "es" ? item.name_es : item.name_en}</div>
                      <div className="text-sm font-bold mt-1">€{(item.price * item.qty).toFixed(2)}</div>
                      <div className="flex items-center gap-2 mt-3">
                        <button data-testid={`qty-decr-${item.id}`} onClick={() => updateQty(item.id, item.qty - 1)} className="w-8 h-8 border-2 border-[#0C0A09] rounded-full flex items-center justify-center bg-white"><Minus size={14} strokeWidth={3} /></button>
                        <span data-testid={`qty-${item.id}`} className="font-bold w-6 text-center">{item.qty}</span>
                        <button data-testid={`qty-incr-${item.id}`} onClick={() => updateQty(item.id, item.qty + 1)} className="w-8 h-8 border-2 border-[#0C0A09] rounded-full flex items-center justify-center bg-[#9BE800]"><Plus size={14} strokeWidth={3} /></button>
                        <button data-testid={`remove-${item.id}`} onClick={() => removeItem(item.id)} className="ml-auto text-xs font-bold uppercase tracking-widest flex items-center gap-1 hover:text-[#FF3D7F]">
                          <Trash2 size={14} strokeWidth={2.5} /> {t.cart.remove}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}
        </div>

        {cart.length > 0 && (() => {
          const totalQty = cart.reduce((s, i) => s + i.qty, 0);
          const freeShip = totalQty >= 2;
          const freeItems = 0;
          const discount = 0;
          const finalTotal = Math.max(0, subtotal - discount);
          const showHint = false;
          return (
          <div className="border-t-[3px] border-[#0C0A09] p-5 bg-white">
            {/* Free shipping progress bar */}
            <div data-testid="free-ship-banner" className={`mb-3 -mx-5 -mt-5 px-5 py-3 border-b-[3px] border-[#0C0A09] ${freeShip ? "bg-[#9BE800]" : "bg-[#FFCC00]"}`}>
              <div className="text-xs font-bold uppercase tracking-[0.18em] text-center">
                {freeShip
                  ? (lang === "es" ? "🎉 ¡ENVÍO GRATIS DESBLOQUEADO!" : "🎉 FREE SHIPPING UNLOCKED!")
                  : (lang === "es" ? `🚚 ¡Añade 1 producto más y envío GRATIS!` : "🚚 Add 1 more item for FREE shipping!")}
              </div>
            </div>
            {discount > 0 && (
              <div data-testid="promo-applied" className="mb-3 -mx-5 -mt-5 px-5 py-3 bg-[#9BE800] border-b-[3px] border-[#0C0A09] flex items-center justify-between">
                <div>
                  <div className="text-xs font-bold uppercase tracking-[0.18em]">🎁 {t.promo.cartLabel}</div>
                  <div className="text-[11px] font-bold mt-0.5">{t.promo.cartUnlocked.replace("{saved}", discount.toFixed(2))}</div>
                </div>
                <div className="font-display text-2xl">−€{discount.toFixed(2)}</div>
              </div>
            )}
            {showHint && (
              <div data-testid="promo-claim-cta" className="mb-3 -mx-5 -mt-5 px-5 py-3 bg-[#FFCC00] border-b-[3px] border-[#0C0A09]">
                <div className="text-xs font-bold uppercase tracking-[0.18em] text-center mb-2">
                  🎁 {lang === "es" ? `¡Estás a 1 vaso de la promo 3×2!` : "1 cup away from buy-2-get-1-free!"}
                </div>
                {!pickFree ? (
                  <button
                    data-testid="claim-free-cup-btn"
                    onClick={() => setPickFree(true)}
                    className="w-full bg-[#0C0A09] text-[#9BE800] border-[3px] border-[#0C0A09] py-2.5 font-display text-xl tracking-wide hover:bg-[#1a1a1a] transition-colors flex items-center justify-center gap-2"
                  >
                    <Gift size={18} strokeWidth={2.75} /> {lang === "es" ? "Reclamar mi vaso GRATIS" : "Claim my FREE cup"}
                  </button>
                ) : (
                  <div data-testid="free-cup-picker">
                    <div className="text-[10px] font-bold uppercase tracking-[0.18em] mb-2 text-center">
                      {lang === "es" ? "Elige el color del vaso gratis" : "Pick your free cup color"}
                    </div>
                    <div className="grid grid-cols-6 gap-2">
                      {FREE_CUP_OPTIONS.map((p) => (
                        <button
                          key={p.id}
                          data-testid={`free-pick-${p.slug}`}
                          onClick={() => claimFreeCup(p)}
                          title={lang === "es" ? p.name_es : p.name_en}
                          className="aspect-square rounded-full border-[3px] border-[#0C0A09] hover:scale-110 transition-transform shadow-[2px_2px_0_0_#0C0A09]"
                          style={{ background: p.hex }}
                          aria-label={p.name_en}
                        />
                      ))}
                    </div>
                    <button
                      onClick={() => setPickFree(false)}
                      className="w-full mt-2 text-[10px] font-bold uppercase tracking-[0.18em] underline"
                    >
                      {lang === "es" ? "Cancelar" : "Cancel"}
                    </button>
                  </div>
                )}
              </div>
            )}

            {discount > 0 && (
              <div className="flex items-center justify-between text-sm mb-1">
                <span className="opacity-60 line-through">€{subtotal.toFixed(2)}</span>
                <span className="text-xs uppercase tracking-widest font-bold text-[#FF3D7F]">−€{discount.toFixed(2)}</span>
              </div>
            )}
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs uppercase tracking-widest font-bold opacity-70">{t.cart.subtotal}</span>
              <span data-testid="cart-subtotal" className="font-display text-3xl">€{finalTotal.toFixed(2)}</span>
            </div>
            <div className="text-xs opacity-70 mb-4">{t.cart.shipping}</div>
            {error && <div data-testid="checkout-error" className="text-sm font-bold text-[#FF3D7F] mb-3">{error}</div>}
            {step === "cart" ? (
              <button data-testid="goto-shipping-btn" onClick={goToShipping} className="nb-btn w-full justify-center">
                {tf.continue} →
              </button>
            ) : (
              <button
                data-testid="checkout-btn"
                onClick={submitOrder}
                disabled={loading}
                className="nb-btn w-full justify-center disabled:opacity-60"
              >
                {loading ? "..." : t.cart.checkout}
              </button>
            )}
            <div className="text-[10px] text-center mt-3 uppercase tracking-[0.2em] opacity-60">
              {lang === "es" ? "Pago seguro con Stripe" : "Secure payment via Stripe"}
            </div>
          </div>
          );
        })()}
      </aside>
    </>
  );
}
